import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Banknote, Search, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { withdrawalApi, type WithdrawalResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";

export const Route = createFileRoute("/admin/withdrawals")({
  component: WithdrawalsPage,
});

const statusColors: Record<string, string> = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

function WithdrawalsPage() {
  const [withdrawals, setWithdrawals] = useState<WithdrawalResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [remark, setRemark] = useState("");

  useEffect(() => {
    fetchWithdrawals();
  }, [statusFilter]);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const data = await withdrawalApi.list(statusFilter || undefined);
      setWithdrawals(data);
    } catch {
      toast.error("Failed to load withdrawals");
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async (id: string, approved: boolean) => {
    setProcessingId(id);
    try {
      await withdrawalApi.approve(id, approved, remark);
      toast.success(`Withdrawal ${approved ? "approved" : "rejected"}`);
      setRemark("");
      fetchWithdrawals();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Update failed";
      toast.error(msg);
    } finally {
      setProcessingId(null);
    }
  };

  const filtered = withdrawals.filter((w) => {
    const s = search.toLowerCase();
    return (
      !s ||
      w.id.toLowerCase().includes(s) ||
      (w.accountName?.toLowerCase() ?? "").includes(s) ||
      (w.bankName?.toLowerCase() ?? "").includes(s) ||
      (w.applicantName?.toLowerCase() ?? "").includes(s)
    );
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Withdrawals</h1>
          <p className="text-sm text-muted-foreground">Approve or reject withdrawal requests.</p>
        </div>
        <Badge variant="secondary" className="gap-1.5">
          <Banknote className="size-3" /> {withdrawals.filter((w) => w.status === "pending").length} pending
        </Badge>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search withdrawals..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 w-64" />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card py-20 text-center shadow-soft">
          <Banknote className="mb-3 size-12 text-muted-foreground/30" />
          <p className="font-semibold text-muted-foreground">No withdrawals found</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border/60 bg-card shadow-soft overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Applicant</th>
                <th className="px-4 py-3 text-left font-medium">Amount</th>
                <th className="px-4 py-3 text-left font-medium">Bank</th>
                <th className="px-4 py-3 text-left font-medium">Account</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Date</th>
                <th className="px-4 py-3 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w) => (
                <tr key={w.id} className="border-t border-border/60 hover:bg-muted/30">
                  <td className="px-4 py-3 font-medium">{w.applicantName ?? "—"}</td>
                  <td className="px-4 py-3 font-semibold">{formatNaira(w.amount)}</td>
                  <td className="px-4 py-3">{w.bankName ?? "—"}</td>
                  <td className="px-4 py-3">
                    <div className="text-xs">{w.accountName ?? "—"}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{w.accountNumber ?? "—"}</div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`text-xs capitalize ${statusColors[w.status] || ""}`}>{w.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(w.createdAt)}</td>
                  <td className="px-4 py-3">
                    {processingId === w.id ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : w.status === "pending" ? (
                      <div className="flex flex-col gap-2">
                        <Input
                          placeholder="Remark (optional)"
                          value={remark}
                          onChange={(e) => setRemark(e.target.value)}
                          className="h-7 text-xs"
                        />
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="h-8 gap-1 text-emerald-600" onClick={() => handleProcess(w.id, true)}>
                            <CheckCircle2 className="size-3.5" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 gap-1 text-red-600" onClick={() => handleProcess(w.id, false)}>
                            <XCircle className="size-3.5" /> Reject
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">{w.remark ?? "—"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
